import { QuotationItemType } from "features/Quotations/constants/quotation.constants";
import type {
  QuotationItem,
  QuotationItemWrite,
} from "features/Quotations/types/quotation.types";

/** Form row shape for BOQ field array */
export type QuotationItemFormRow = {
  id?: number;
  type: QuotationItemType;
  description: string;
  quantity: number;
  unit: string;
  rate: number;
  /** Purchase-order specific — ignored by quotation / material-request BOQ. */
  supplierId?: number | null;
  /** Links this line back to the MR item it was created from. */
  materialRequestItemId?: number | null;
};

/** Map API / legacy item into form defaults (supports transitional `qty`). */
export function mapQuotationItemToFormRow(
  item: QuotationItem & { qty?: number },
): QuotationItemFormRow {
  const quantityRaw = item.quantity ?? item.qty ?? 1;
  const quantity =
    Number.isFinite(Number(quantityRaw)) && Number(quantityRaw) > 0
      ? Number(quantityRaw)
      : 1;
  const rateRaw = item.rate ?? 0;
  const rate = Number.isFinite(Number(rateRaw)) ? Number(rateRaw) : 0;
  const description = item.description?.trim() || "";
  const unit = item.unit?.trim() || "";
  return {
    id: item.id,
    type: item.type ?? QuotationItemType.MATERIAL,
    description,
    quantity,
    unit,
    rate,
  };
}

export function formRowsToWritePayload(
  rows: QuotationItemFormRow[],
): QuotationItemWrite[] {
  return rows.map((row) => {
    const base: QuotationItemWrite = {
      description: row.description.trim(),
      type: row.type,
      quantity: Number(row.quantity),
      unit: row.unit.trim(),
      rate: Number(row.rate),
    };
    if (row.id != null && row.id > 0) base.id = row.id;
    return base;
  });
}

/** Prefer stored amount from API; otherwise quantity × rate (form preview). */
export function quotationLineAmount(
  item: Pick<QuotationItem, "quantity" | "rate"> & { amount?: number },
): number {
  const a = item.amount;
  if (a != null && Number.isFinite(Number(a))) return Number(a);
  return Number(item.quantity) * Number(item.rate);
}

export function quotationItemsSubtotal(items: QuotationItem[] | undefined): number {
  if (!items?.length) return 0;
  return Number(
    items.reduce((sum, row) => sum + quotationLineAmount(row), 0).toFixed(2),
  );
}
